// ⛔ TOUTE CLÉ LISTÉE ICI EST PUBLIQUE.
//
// `debateConfig` est importé par du code CLIENT (`_app.tsx`, `LoadGraphLogin`,
// `ThirdParties`, `groupsConfig`, `logrocket`, `useWindowMessagesLogger`…).
// Next.js inline donc l'objet ENTIER, valeurs comprises, dans
// `.next/static/chunks/` — servi publiquement, sans authentification, et
// accompagné de sa source map (`productionBrowserSourceMaps`).
//
// ⛔ NE JAMAIS revenir à `{ ...env }`. C'est ce spread qui a produit la fuite du
// 2026-09-21 : il répandait le fichier `.env` de build en entier, secrets
// compris, dans le bundle public. Une liste explicite fait que le PROCHAIN
// secret ajouté à `.env` n'y retombe pas par défaut — il faudra l'écrire ici,
// donc le décider. Le garde-fou CI (`scripts/ci/check-client-bundle-secrets.sh`)
// est la ceinture ; cette liste est l'architecture.
//
// Un secret dont le front a besoin n'existe pas : ce qui atteint le navigateur
// est public. S'il faut une vérification, elle se fait côté serveur.
let env: EnvFile;
try {
  // eslint-disable-next-line import/extensions
  env = require('./.env');
} catch {
  env = {
    enableCache: false,
  } as EnvFile;
}
// ⛔ AUCUNE valeur de repli vers un hôte de DEV (B2_39). Le secret de build
// prod ne portait pas `graphloginUrl` (constaté le 2026-10-03, déjà absent
// avant la migration) : la route SSO retombait sur `devlogin`, avec des jetons
// de prod dans l'URL. Une clé absente
// reste `undefined` ; `build-prod` refuse le build si `graphloginUrl` ou
// `integrationUrl` manque (`scripts/ci/check-required-env-keys.js`).
const integrationUrl =
  (env.useLocalIntegration === true ? env.integrationUrlLocal : env.integrationUrl) ||
  env.integrationUrl;

const graphloginUrl =
  (env.useLocalIntegration === true ? env.graphloginUrlLocal : env.graphloginUrl) ||
  env.graphloginUrl;

const isDev = env.isDev === true || false;
const { apiUrl } = env;

export const debateConfig = {
  // ── Reprises explicites du fichier `.env` (ex-spread `...env`) ──────────────
  env: env.env,
  noIndex: env.noIndex,
  enableCache: env.enableCache,
  shouldPreventCustomHead: env.shouldPreventCustomHead,
  shouldPreventThirdParties: env.shouldPreventThirdParties,
  graphloginUrlLocal: env.graphloginUrlLocal,
  integrationUrlLocal: env.integrationUrlLocal,
  useLocalIntegration: env.useLocalIntegration,
  websiteKey: env.websiteKey,
  __websiteKey: env.__websiteKey,
  SHOW_BURGER_MENU: env.SHOW_BURGER_MENU,
  HANDLE_DEBUG_STACK_LOGS: env.HANDLE_DEBUG_STACK_LOGS,
  ENABLE_DEBUG_POST_MESSAGES: env.ENABLE_DEBUG_POST_MESSAGES,
  ISR_REVALIDATE: env.ISR_REVALIDATE,
  BASE_PATH: env.BASE_PATH,

  // ⚠ Jeton partagé, lu par du code CLIENT (`utils/ssrRevalidate.ts`, importé par
  // `HeaderLogo`, `thread-utils`, `EditorWriteFrom`) ET par la route serveur
  // `pages/api/revalidate.ts`, qui le compare en égalité stricte. Il est donc
  // dans le bundle public — et il l'était déjà avant ce découpage.
  // ⛔ Le sortir d'ici CASSE la purge ISR déclenchée depuis le front : cela
  // suppose de refaire l'autorisation de `/api/revalidate` (session +
  // `public-key`), ce qui dépasse ce ticket. Décision Mathias 2026-09-21 :
  // hors périmètre B2_14, garde-fou CI inchangé, ticket séparé.
  ISR_REVALIDATE_TOKEN: env.ISR_REVALIDATE_TOKEN,

  // ── Valeurs dérivées et défauts ────────────────────────────────────────────
  isDev,
  publicKeyForDev: env.websiteKey || 'no-key3',
  language: 'fr',
  debug: process.env.DEBUG || env.debug || false,
  cacheEnabled: process.env.CACHE_ENABLED || env.cacheEnabled || false,
  cacheDebug: process.env.CACHE_DEBUG || env.cacheDebug || false,
  port: env.port || 3000,
  wsUrl: env.wsUrl,
  apiUrl,
  imageUrl: env.imageUrl,
  yakliUrl: env.yakliUrl,
  translationsFile: '.translations.json',
  prettyUrls: true, // should always be enabled
  pyschoPath: env.pyschoPath || 'https://www.psychologies.com/psy_polish/look-a-like/', // should always be enabled
  graphloginUrl,
  integrationUrl,
  SPEED_UP_PARTIAL_SSR: env.SPEED_UP_PARTIAL_SSR || false,
  enablePWAInstallPrompt: false,
  enableAndroidPWAInstallPrompt: true,
  enableNotifInDev: true,
  enableLogRocket: false /* isDev || apiUrl.includes('dev.graphcomment.com') */,
} as const;

// export default debateConfig;

interface EnvFile {
  env?: string;
  isDev: boolean;
  noIndex?: boolean;
  enableCache: boolean;
  shouldPreventCustomHead: boolean;
  shouldPreventThirdParties: boolean;
  graphloginUrl?: string;
  graphloginUrlLocal: string;
  integrationUrl?: string;
  integrationUrlLocal: string;
  useLocalIntegration?: boolean;
  websiteKey?: string;
  __websiteKey?: string;
  publicKeyForDev?: string;
  SPEED_UP_PARTIAL_SSR: boolean;
  SHOW_BURGER_MENU?: boolean;
  HANDLE_DEBUG_STACK_LOGS?: boolean;
  ISR_REVALIDATE?: number;
  ISR_REVALIDATE_TOKEN?: string;
  BASE_PATH?: string;
  // Other keys used in the config, not defined yet in the env file:
  debug?: boolean;
  cacheEnabled?: boolean;
  cacheDebug?: boolean;
  port?: number;
  wsUrl?: string;
  apiUrl?: string;
  imageUrl?: string;
  yakliUrl?: string;
  pyschoPath?: string;
  ENABLE_DEBUG_POST_MESSAGES?: boolean;
  // ⛔ SIXIÈME CLÉ RETIRÉE le 2026-09-22 : `captchaApiKey`. Littéral de 40
  // caractères écrit EN DUR ici (donc dans l'historique git ET dans tous les
  // bundles publics depuis son ajout), et AUCUN lecteur dans le monorepo — le
  // reCAPTCHA du back-office a sa propre clé via `angular-recaptcha`. La passe
  // du 21/09 l'a manquée parce qu'elle traitait les clés venues du `.env`, pas
  // les littéraux en dur : c'est l'angle mort que le ticket `B2_18` ferme.
  //
  // ⛔ NE PAS l'ajouter à `scripts/ci/client-bundle-forbidden-names.txt` : la
  // liste est partagée avec le filtre de PRÉVENTION, et son nom est cité juste
  // au-dessus — le garde-fou passerait le build au rouge sur ce commentaire via
  // la source map. Et si c'est une *site key* (publique par conception), le
  // bannir casserait un usage légitime futur.
  //
  // ⛔ CINQ CLÉS RETIRÉES le 2026-09-21 — noms exacts et justification par clé
  // dans le ticket `hosting-migration:B2_14`. En résumé : le DSN Mongo, le
  // chemin d'images, les deux clés Loco et le secret de signature du jeton
  // natif. Quatre n'avaient AUCUN lecteur dans le dépôt ; la cinquième est
  // passée côté serveur (drapeau `X-Native-App`, api `src/utils/nativeApp.js`).
  //
  // ⛔ NE PAS LES RÉINTRODUIRE — les remettre ici les remet dans le bundle
  // public. En particulier, ne pas remettre la clé Loco « parce que le build en
  // dépend » : `scripts/prepare-build.js` lit une VARIABLE D'ENVIRONNEMENT, pas
  // cette configuration.
  //
  // ⛔ ET NE PAS LES NOMMER ICI, même en commentaire : `productionBrowserSourceMaps`
  // est actif, la source map embarque le texte source, et le garde-fou CI grep
  // aussi les `.js.map`. Écrire le nom d'un secret dans ce fichier fait échouer
  // le build — constaté le 2026-09-21.
}
